<template>
  <form class="auth-step" @submit.prevent="handleSubmit">
    <div class="auth-heading">
      <i class="bi bi-person-circle auth-brand-icon-placeholder"></i>
      <h3 class="auth-title">Welcome Back</h3>
      <p class="auth-subtitle">Sign in to continue your journey</p>
    </div>

    <div v-if="signupSuccessMessage" class="auth-error" style="background: #f0fdf4; border-color: #bbf7d0; color: #16a34a;">
      <i class="bi bi-check-circle"></i>
      Account created successfully! Please log in.
    </div>

    <div v-if="errorMessage" class="auth-error">
      <i class="bi bi-exclamation-circle"></i>
      {{ errorMessage }}
    </div>

    <div class="form-inner two mb-20">
      <label><i class="bi bi-phone me-1"></i>Mobile No.</label>
      <div class="mobile-input-row">
        <span class="mobile-prefix">+91</span>
        <input
          :value="mobileDigits"
          type="tel"
          placeholder="Enter mobile number"
          autocomplete="tel"
          maxlength="10"
          @input="onMobileInput"
        >
      </div>
    </div>

    <div class="form-inner two mb-10">
      <label><i class="bi bi-lock me-1"></i>Password</label>
      <div class="password-wrapper">
        <input
          :value="password"
          :type="showPassword ? 'text' : 'password'"
          placeholder="Enter password"
          autocomplete="current-password"
          @input="onPasswordInput"
        >
        <button type="button" class="password-toggle" @click="showPassword = !showPassword">
          <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
        </button>
      </div>
    </div>

    <div class="text-end mb-20">
      <a href="#" class="forgot-link" @click.prevent="$emit('forgot-password')">Forgot Password?</a>
    </div>

    <button type="submit" class="primary-btn1 auth-submit-btn mb-16" :disabled="busy">
      <span>{{ busy ? 'Signing in...' : 'Sign In' }}</span>
    </button>

    <div class="auth-divider">or continue with</div>

    <div class="sso-placeholder mb-16">
      <button type="button" class="sso-btn" disabled>
        <i class="bi bi-google"></i>
      </button>
      <button type="button" class="sso-btn" disabled>
        <i class="bi bi-facebook"></i>
      </button>
    </div>

    <p class="auth-switch">
      Don't have an account?
      <button type="button" @click="$emit('switch-mode')">Sign Up</button>
    </p>
  </form>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  mobile: {
    type: String,
    default: ''
  },
  password: {
    type: String,
    default: ''
  },
  status: {
    type: String,
    default: 'idle'
  },
  errorMessage: {
    type: String,
    default: ''
  },
  signupSuccessMessage: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['submit', 'update:mobile', 'update:password', 'switch-mode', 'forgot-password'])

const showPassword = ref(false)

const busy = computed(() => props.status === 'logging_in')

const mobileDigits = computed(() => {
  const digits = String(props.mobile ?? '').replace(/\D/g, '')
  if (digits.startsWith('91') && digits.length >= 12) {
    return digits.slice(-10)
  }
  if (digits.startsWith('0') && digits.length === 11) {
    return digits.slice(1)
  }
  return digits.slice(-10)
})

const onMobileInput = (event) => {
  const digits = String(event.target.value ?? '').replace(/\D/g, '').slice(0, 10)
  emit('update:mobile', digits)
}

const onPasswordInput = (event) => {
  emit('update:password', event.target.value)
}

const handleSubmit = () => {
  emit('submit')
}
</script>

<style scoped>
.auth-brand-icon-placeholder {
  font-size: 48px;
  color: var(--primary-color1, #23b5ee);
  display: block;
  text-align: center;
  margin-bottom: 12px;
}

.forgot-link {
  font-size: 13px;
  color: var(--primary-color1, #23b5ee);
  text-decoration: none;
  font-weight: 500;
}

.forgot-link:hover {
  text-decoration: underline;
}

.mb-16 {
  margin-bottom: 16px;
}

.password-wrapper {
  position: relative;
}

.password-wrapper input {
  padding-right: 40px;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  font-size: 16px;
  line-height: 1;
}

.password-toggle:hover {
  color: #333;
}

.mobile-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-prefix {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
  line-height: 48px;
  padding: 0 10px;
  background: #f9fafb;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
}

.sso-placeholder {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.sso-btn {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #6b7280;
  cursor: not-allowed;
  opacity: 0.5;
}
</style>
